<template>
  <div class="saved-lists-table">
    <mi-table
      :columns="columns"
      :rows="rows"
      :loading="loading"
      class="saved-lists-table__content"
      loading-label="Loading lists. Please wait..."
      no-data-label="Your saved lists will be displayed here."
      row-key="id"
      hide-pagination
      sticky-header
      striped
      wrap-cells
    >
      <template #body-cell-actions="{ row }">
        <mi-td>
          <mi-btn
            class="action-btn"
            color="transparent"
            @click.stop
          >
            <q-icon size="small">
              <img src="@/assets/images/dots_vertical.svg" alt="" />
            </q-icon>
            <mi-menu>
              <q-list class="actions-column__list">
                <q-item
                  v-if="canDownloadExport(row)"
                  v-close-popup
                  clickable
                  @click="onHandleDownload(row.status, row.id, row.exportId)"
                >
                  <q-item-section>
                    <q-item-label>Download export</q-item-label>
                    <q-item-label
                      v-if="row.exportedDate"
                      class="actions-column__generated-date"
                    >
                      (Generated {{ formatExportedDate(row.exportedDate) }})
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-item
                  v-if="canExport(row)"
                  v-close-popup
                  clickable
                  @click="handleOpenExportListModal(row.id)"
                >
                  <q-item-section>
                    <q-item-label>Generate new export</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  clickable
                  @click="handleOpenRemoveConfigurationsModal(row)"
                >
                  <q-item-section>
                    <q-item-label>Remove configurations</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  clickable
                  @click="handleCloseDeleteModal(row.id)"
                >
                  <q-item-section>
                    <q-item-label class="actions-column__delete-label">Delete list</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </mi-menu>
          </mi-btn>
        </mi-td>
      </template>
      <template #body-cell-status="{ value }">
        <mi-td class="status-cell">
          <man-tag class="status-cell__tag" :variant="getStatusVariant(value)" size="m">
            {{ SAVED_LISTS_STATUS[value].label }}
          </man-tag>
        </mi-td>
      </template>
    </mi-table>
  </div>
  <delete-confirmation-modal
    :row-id="selectedRowId"
    :delete-modal="deleteModal"
    :handle-delete-list-configuration="(id) => handleDeleteListConfiguration(id, handleCloseDeleteModal)"
    :handle-close-delete-modal="handleCloseDeleteModal"
  ></delete-confirmation-modal>
  <export-list-options-modal
    :show-modal="openExportListOptionsModal"
    :list-id="listIdSelected"
    :on-handle-export="onHandleExport"
    @update:show-modal="openExportListOptionsModal = false"
  >
  </export-list-options-modal>
  <remove-configurations-from-list-modal
    v-if="openRemoveConfigurationsModal"
    :show-modal="openRemoveConfigurationsModal"
    :list="listSelected"
    :refresh-saved-lists-table="refreshTable"
    @update:show-modal="openRemoveConfigurationsModal = false"
  >
  </remove-configurations-from-list-modal>

  <div>
    <mi-overlay :model-value="isExportInProgress && !skipWaiting"></mi-overlay>
    <mi-notify :model-value="isExportInProgress && !skipWaiting">
      <template #inner-content>
        <div>
          <p class="q-ma-none text-weight-bold">
            Export in progress.
          </p>
          <mi-loader></mi-loader>
          <div class="skip-waiting-notification">
            <span class="skip-waiting-notification__text">This action make take longer than expected</span>
            <mi-button class="skip-waiting-notification__skip-text" flat @click.stop="handleSkipWaiting()">
              Skip waiting
            </mi-button>
          </div>
        </div>
      </template>
    </mi-notify>
  </div>
</template>

<script>
  import { provide, ref } from 'vue'
  import ExportListOptionsModal from '@/components/pac/saved-lists/ExportListOptionsModal.vue'
  import { exportConfigurationsList, getExportConfigurationsListStatus } from '@/api'
  import notify from '@/utils/notify'
  import {
    SAVED_LISTS_COLUMNS,
    SAVED_LISTS_EXPORT_OPTIONS_PROVIDER,
    SAVED_LISTS_STATUS
  } from '@/components/pac/saved-lists/utils'
  import { triggerDownload } from '@/utils/triggerDownload'
  import RemoveConfigurationsFromListModal from '@/components/pac/saved-lists/RemoveConfigurationsFromListModal.vue'
  import DeleteConfirmationModal from '@/components/pac/saved-lists/dialogs/DeleteConfirmationModal.vue'
  import formatDate from '@/utils/formatDate'

  export default {
    name: 'SavedListsTable',
    components: { RemoveConfigurationsFromListModal, DeleteConfirmationModal, ExportListOptionsModal },
    props: {
      rows: {
        type: Array,
        required: false,
        default: () => []
      },
      loading: {
        type: Boolean,
        required: false,
        default: false
      },
      handleDeleteListConfiguration: {
        type: Function,
        required: true
      },
      refreshTable: {
        type: Function,
        required: true,
        default: () => ({})
      }
    },
    setup(props) {
      const deleteModal = ref(false)
      const selectedRowId = ref('')
      const openExportListOptionsModal = ref(false)
      const openRemoveConfigurationsModal = ref(false)
      const listIdSelected = ref('')
      const listSelected = ref({})
      const isExportInProgress = ref(false)
      const selectedOptions = ref({
        yellow: true,
        green: true
      })

      const columns = ref(SAVED_LISTS_COLUMNS)

      const skipWaiting = ref(false)

      provide(SAVED_LISTS_EXPORT_OPTIONS_PROVIDER, selectedOptions)

      const canDownloadExport = row => [
        SAVED_LISTS_STATUS.READY_TO_DOWNLOAD.key,
        SAVED_LISTS_STATUS.EXPORTED.key
      ].includes(row.status) && Boolean(row.exportId)

      const canExport = row => ![
        SAVED_LISTS_STATUS.IN_PROGRESS.key
      ].includes(row.status)

      const formatExportedDate = exportedDate => {
        if (!exportedDate) return ''

        return formatDate(exportedDate).replace(', ', ' · ')
      }

      // eslint-disable-next-line security/detect-object-injection
      const getStatusVariant = status => SAVED_LISTS_STATUS[status]?.color || 'success'

      const handleOpenExportListModal = listId => {
        openExportListOptionsModal.value = true
        listIdSelected.value = listId
      }

      const handleOpenRemoveConfigurationsModal = list => {
        listSelected.value = list
        openRemoveConfigurationsModal.value = true
      }

      const handleSkipWaiting = () => {
        skipWaiting.value = true
      }

      const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

      const getPollingDelay = attempt => Math.min(3000 + ((attempt - 1) * 2000), 10000)

      const pollExportStatus = async (listId, exportId, attempt = 1) => {
        if (skipWaiting.value) {
          skipWaiting.value = false
          return null
        }

        if (attempt > 100) {
          throw new Error('Export timeout')
        }

        const exportStatus = await getExportConfigurationsListStatus({
          exportId,
          listId
        })

        if (
          exportStatus.status === SAVED_LISTS_STATUS.READY_TO_DOWNLOAD.key
          || exportStatus.status === SAVED_LISTS_STATUS.EXPORTED.key
        ) {
          await wait(1500)
          return exportStatus
        }

        if (exportStatus.status === SAVED_LISTS_STATUS.FAILED.key) {
          throw new Error('Export failed')
        }

        await wait(getPollingDelay(attempt))

        return pollExportStatus(listId, exportId, attempt + 1)
      }

      const downloadExportedList = exportStatus => {
        triggerDownload(exportStatus.exportLink)
        props.refreshTable()
      }

      const onHandleExport = async () => {
        try {
          isExportInProgress.value = true

          const result = await exportConfigurationsList({
            yellowConfiguration: selectedOptions.value.yellow,
            greenConfiguration: selectedOptions.value.green,
            listId: listIdSelected.value
          })

          openExportListOptionsModal.value = false

          props.refreshTable()
          const exportStatus = await pollExportStatus(listIdSelected.value, result.exportId)
          if (!exportStatus) {
            return
          }
          downloadExportedList(exportStatus)
        }
        catch (err) {
          console.error(err)
        }
        finally {
          isExportInProgress.value = false
        }
      }

      const onHandleDownload = async (status, listId, exportId) => {
        if (status === SAVED_LISTS_STATUS.IN_PROGRESS.key) {
          notify({
            title: 'Error',
            content: `The validation for ${ listId } is still in progress`,
            type: 'negative'
          })
          return
        }
        const exportStatus = await getExportConfigurationsListStatus({
          exportId,
          listId
        })
        if (!exportStatus?.exportLink) {
          notify({
            title: 'Error',
            content: 'Error trying to download.',
            type: 'negative'
          })
          return
        }
        downloadExportedList(exportStatus)
      }

      const handleCloseDeleteModal = id => {
        if (id) selectedRowId.value = id
        deleteModal.value = !deleteModal.value
      }

      return {
        columns,
        getStatusVariant,
        handleCloseDeleteModal,
        deleteModal,
        selectedRowId,
        handleOpenExportListModal,
        openExportListOptionsModal,
        isExportInProgress,
        listIdSelected,
        onHandleExport,
        onHandleDownload,
        skipWaiting,
        handleSkipWaiting,
        downloadExportedList,
        handleOpenRemoveConfigurationsModal,
        openRemoveConfigurationsModal,
        listSelected,
        canDownloadExport,
        canExport,
        formatExportedDate
      }
    },
    computed: {
      SAVED_LISTS_STATUS() {
        return SAVED_LISTS_STATUS
      }
    }
  }
</script>

<style lang="scss" scoped>
  .saved-lists-table {
    width: 100%;
    position: relative;

    .q-table__container {
      max-height: calc(100vh - 280px);
    }

    &__content {
      ::v-deep(.q-table__middle) {
        min-height: 400px;
        max-height: 65vh;
      }

      ::v-deep(.q-inner-loading) {
        top: 50%;
        transform: translateY(-50%);
      }

      ::v-deep(tbody tr) {
        cursor: default;
      }
    }
  }

  .status-cell {
    // stylelint-disable declaration-no-important
    &__tag {
      font-size: 14px !important;
      font-weight: 400 !important;
    }
  }

  ::v-deep(.custom-notification__content) {
    width: 350px !important;
  }

  .skip-waiting-notification {
    display: flex;
    flex-direction: column;

    &__skip-text {
      font-size: 14px;
      margin-bottom: 8px;
      text-decoration: underline;
      cursor: pointer;
    }

    &__text {
      margin-top: 8px;
      margin-bottom: 24px;
      font-size: 14px;
    }
  }

  .actions-column__list {
    width: 100%;

    ::v-deep(.q-item) {
      width: 100%;
    }

    ::v-deep(.q-item__section) {
      align-items: center;
      text-align: center;
    }

    ::v-deep(.q-item__label) {
      width: 100%;
      text-align: center;
    }
  }

  .actions-column__delete-label {
    color: $mi-red-600;
  }

  .actions-column__generated-date {
    font-size: 12px;
  }
</style>
